import React from 'react';
import * as ToggleGroupPrimitive from '@radix-ui/react-toggle-group';
import { cn } from '../../utils/cn';

/**
 * ToggleGroupItem Component. Extends Radix UI's ToggleGroupItem.
 *
 * An individual item within a ToggleGroup. It represents a selectable option.
 * This component must be used as a child of a ToggleGroup component.
 * Learn more about Radix UI ToggleGroup: https://www.radix-ui.com/primitives/docs/components/toggle-group
 *
 * @component
 * @param {Object} props - The component props
 * @param {string} [props.className] - Additional CSS classes to apply to the item
 * @param {'first' | 'last' | 'middle'} props.position - The position of the item within the group
 * @param {React.ReactNode} props.children - The content to render within the item
 *
 * @example
 * <ToggleGroup>
 *   <ToggleGroupItem value="option" position="middle">
 *     Option Label
 *   </ToggleGroupItem>
 * </ToggleGroup>
 */
const ToggleGroupItem = React.forwardRef<
  React.ElementRef<typeof ToggleGroupPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof ToggleGroupPrimitive.Item> & {
    position: 'first' | 'last' | 'middle';
    className?: string;
  }
>(({ className, children, position, ...props }, ref) => {
  return (
    <ToggleGroupPrimitive.Item
      ref={ref}
      className={cn(
        // Layout and spacing
        'inline-flex items-center gap-2',
        'h-xl px-3 py-2',

        // Typography
        'text-sm font-medium',
        'whitespace-nowrap',

        // Colors
        'bg-white dark:bg-gray-800',
        'hover:bg-gray-100 dark:hover:bg-gray-700',
        'data-[state=on]:bg-action-background',
        'data-[state=on]:text-action-primary',

        // Borders and rings
        'ring-light-gray ring-1',
        'data-[state=on]:ring-action-primary',
        'data-[state=on]:ring-1',
        'data-[state=on]:ring-offset-[0px]',
        '-outline-offset-2',

        // Z-index
        'data-[state=on]:z-[1]',

        // Add disabled styles
        'disabled:opacity-50',
        'disabled:cursor-not-allowed',
        'disabled:hover:bg-transparent',

        // Position-specific styles
        position === 'first' && 'rounded-l-[10px]',
        position === 'last' && 'rounded-r-[10px]',
        position === 'middle' && 'border-x-0',

        // Custom className
        className,
      )}
      {...props}
    >
      {children}
    </ToggleGroupPrimitive.Item>
  );
});

ToggleGroupItem.displayName = ToggleGroupPrimitive.Item.displayName;

export { ToggleGroupItem };
